import React, { useState } from "react";
import * as Mui from "@mui/material";
import { themes } from "services/constants";
import { Delete, Edit, Description, Cancel, Info } from "@mui/icons-material";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { EditBankDetails } from "./Bulk_edit_bank_details";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Loader } from "components/Loader";

let multiApiCallRestriction = true;

export const BulkBankImportData = ({
  header,
  importedData,
  setImportData,
  page,
  setPage,
  fileName,
  clearImportedData,
  fileSize,
  setBulkData,
  bulkData,
  buttonDisabled,
  setButtonDisabled,
  setShowListView,
}) => {
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [removeDialogBox, setRemoveDialogBox] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState([]);
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [editBankDetails, setEditBankDetails] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const setDeleteBankDetails = async () => {
    const remainingData = importedData.filter(
      (details) => details.employee_id !== deleteRecord.employee_id
    );
    if (remainingData.length === 0) {
      clearImportedData();
    } else {
      setImportData(remainingData);
    }
    // FIX 9: use is_valid consistently — same check as handleFileUpload and EditBankDetails
    setButtonDisabled(remainingData.some((r) => r.is_valid === false));
    setRemoveDialogBox(false);
  };

  const CreateBankRecords = async () => {
    if (multiApiCallRestriction) {
      setLoading(true);
      multiApiCallRestriction = false;

      fetch(`${domain}bulk-bank-process/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      })
        .then((r) => r.json())
        .then((response) => {
          if (response?.message) {
            showToast("success", "Bank details imported.");
            history.push("/admin/hrzone/employees");
            setShowListView(false);
          } else {
            showToast("error", "Couldn't process the bank details. Please try again.");
          }
        })
        .catch((error) => showToast("error", "Couldn't save the bank details. Please try again."))
        .finally(() => {
          multiApiCallRestriction = true;
          setLoading(false);
        });
    }
  };

  return (
    <Mui.Grid container>
      <Mui.Grid container sx={{ paddingBottom: 2 }}>
        <Mui.Grid xs={2} sm={2} md={2.5} lg={3} xl={3}>
          <Mui.Grid container sx={{ border: `1px solid ${themes.shadowText}`, borderRadius: 4 }}>
            <Mui.Grid xs={2} sm={2} md={2.5} lg={2.5} xl={3} sx={{ paddingTop: 1.5, paddingLeft: 1 }}>
              <Description fontSize="large" sx={{ color: themes.primaryIndicator, fontSize: 45 }} />
            </Mui.Grid>
            <Mui.Grid xs={8} sm={8} md={7} lg={7} xl={6} sx={{ paddingTop: 1 }}>
              <Mui.Stack>
                <Mui.Typography>{fileName}</Mui.Typography>
                <Mui.Typography>{fileSize}</Mui.Typography>
              </Mui.Stack>
            </Mui.Grid>
            <Mui.Grid xs={2} sm={2} md={2.5} lg={2.5} xl={3} sx={{ paddingTop: 1, paddingLeft: 1 }}>
              <Mui.IconButton onClick={() => clearImportedData()}>
                <Cancel fontSize="small" sx={{ color: themes.headLine, fontSize: 45 }} />
              </Mui.IconButton>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>

        <Mui.Grid
          xs={2} sm={2} md={9.5} lg={9} xl={9}
          sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", paddingRight: 1 }}
        >
          <Mui.Button
            variant="contained"
            style={{
              alignSelf: "flex-end",
              marginRight: 20.0,
              width: 150,
              // FIX 9: button is always visible; only disabled state changes colour
              backgroundColor: buttonDisabled ? themes.shadowColor : themes.primaryIndicator,
              color: themes.headLine,
              textTransform: "capitalize",
            }}
            onClick={() => CreateBankRecords()}
            disabled={buttonDisabled}
          >
            <Mui.Typography sx={{ fontSize: { xs: 0, sm: 0, md: 12, lg: 14 } }}>
              Import Bank Data
            </Mui.Typography>
          </Mui.Button>
        </Mui.Grid>
      </Mui.Grid>

      {!loading ? (
        <Mui.TableContainer component={Mui.Paper} sx={{ width: "100%", maxHeight: 840, paddingRight: 1 }}>
          <Mui.Table>
            <Mui.TableHead sx={{ backgroundColor: themes.primary, position: "sticky", top: -1, zIndex: 10 }}>
              <Mui.TableRow sx={{ "& .MuiTableCell-root": { height: "15px", padding: "6px 6px" } }}>
                {header.map((head) => (
                  <Mui.TableCell key={head} sx={{ color: themes.headLine, fontWeight: "bold" }}>
                    {head}
                  </Mui.TableCell>
                ))}
                <Mui.TableCell sx={{ color: themes.headLine, fontWeight: "bold" }}>Status</Mui.TableCell>
                <Mui.TableCell sx={{ color: themes.headLine, fontWeight: "bold" }}>Actions</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {importedData?.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={header.length + 2} align="center">
                    <Mui.Alert severity="info">No data available</Mui.Alert>
                  </Mui.TableCell>
                </Mui.TableRow>
              ) : (
                importedData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((data, index) => (
                    <Mui.TableRow
                      key={index}
                      sx={{
                        "& .MuiTableCell-root": { height: "15px", padding: "6px 6px" },
                        backgroundColor: data?.status === "Invalid" ? themes.backgroundRed : null,
                      }}
                    >
                      <Mui.TableCell>{data?.employee_id || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.user_name || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.bankname || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.accountno || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.ifsc || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.branch_name || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{data?.status || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>
                        <Mui.Stack direction="row">
                          <Mui.IconButton
                            onClick={() => {
                              setEditBankDetails(data);
                              setOpenSidePeek(true);
                              setReloadData(true);
                            }}
                          >
                            <Edit />
                          </Mui.IconButton>
                          <Mui.IconButton
                            onClick={() => {
                              setDeleteRecord(data);
                              setRemoveDialogBox(true);
                            }}
                          >
                            <Delete />
                          </Mui.IconButton>
                          {data?.status === "Invalid" && (
                            <Mui.Tooltip title={data.error_messages}>
                              <Mui.IconButton>
                                <Info />
                              </Mui.IconButton>
                            </Mui.Tooltip>
                          )}
                        </Mui.Stack>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ))
              )}
            </Mui.TableBody>
          </Mui.Table>
          {importedData.length > 10 && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[10, 20, 50, 100]}
              count={importedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Mui.TableContainer>
      ) : (
        <Loader info="Processing..." />
      )}

      <GlobalDialogBox
        handleCloseDialog={() => setRemoveDialogBox(false)}
        open={removeDialogBox}
        title="Remove Record"
      >
        <Mui.Typography>
          Do you want to remove the bank record for:{" "}
          <span style={{ color: themes.redColor }}>
            {deleteRecord.user_name || deleteRecord.employee_id}
          </span>{" "}
          ?
        </Mui.Typography>
        <Mui.Grid container sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
          <CustomButton actionFuntion={() => setRemoveDialogBox(false)} actionName="No" typeName="button" />
          &nbsp;&nbsp;
          <CustomButton actionFuntion={setDeleteBankDetails} actionName="Yes" typeName="button" />
        </Mui.Grid>
      </GlobalDialogBox>

      {openSidePeek && (
        <EditBankDetails
          openSidePeek={openSidePeek}
          setOpenSidePeek={setOpenSidePeek}
          editBankDetails={editBankDetails}
          reloadData={reloadData}
          setReloadData={setReloadData}
          importedData={importedData}
          setImportData={setImportData}
          bulkData={bulkData}
          setBulkData={setBulkData}
          setButtonDisabled={setButtonDisabled}
        />
      )}
    </Mui.Grid>
  );
};